import React from "react";
import { Box, Typography } from "@mui/material";
import { C, FONT, clamp, eyebrowSx } from "../accountStyles";
import { ArtworkThumb, CardButton, StatusTag } from "./primitives";



const TONE_COLOR = { success: C.success, danger: C.danger };

export default function LotCard({
  onClick,
  image,
  imageAlt = "",
  ratio = "4 / 5",
  dimmed = false,
  tag, // { label, tone }
  eyebrow,
  artist,
  artistLines = 1,
  title,
  footerLabel,
  footerValue,
  footerTone,
  note, // { text, tone }
}) {
  return (
    <CardButton onClick={onClick}>
      <ArtworkThumb src={image} alt={imageAlt} ratio={ratio} dimmed={dimmed}>
        {tag && (
          <StatusTag label={tag.label} tone={tag.tone} sx={{ position: "absolute", top: 10, left: 10, bgcolor: C.paper }} />
        )}
      </ArtworkThumb>

      <Box sx={{ p: { xs: 1.5, md: 2 }, display: "flex", flexDirection: "column", flexGrow: 1, gap: 1.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography component="div" sx={{ ...eyebrowSx, ...clamp(1), minHeight: "1.5em" }}>
            {eyebrow}
          </Typography>
          <Typography
            component="div"
            className="acc-title"
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: "0.85rem", md: "0.95rem" },
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: C.ink,
              lineHeight: 1.3,
              ...clamp(artistLines),
              minHeight: `${1.3 * artistLines}em`,
            }}
          >
            {artist}
          </Typography>
          <Typography
            component="div"
            sx={{ fontFamily: FONT, fontStyle: "italic", fontSize: { xs: "0.85rem", md: "0.92rem" }, color: "#444", lineHeight: 1.35, ...clamp(2), minHeight: "2.7em" }}
          >
            {title}
          </Typography>
        </Box>

        <Box sx={{ mt: "auto", pt: 1.5, borderTop: `1px solid ${C.line}` }}>
          {footerLabel && <Typography component="div" sx={{ ...eyebrowSx, fontWeight: 400 }}>{footerLabel}</Typography>}
          <Typography
            component="div"
            sx={{ fontFamily: FONT, fontWeight: 700, fontSize: { xs: "0.95rem", md: "1.05rem" }, color: TONE_COLOR[footerTone] || C.ink, wordBreak: "break-word" }}
          >
            {footerValue}
          </Typography>
          {note && note.text && (
            <Typography component="div" sx={{ ...eyebrowSx, fontSize: "0.62rem", mt: 0.5, color: TONE_COLOR[note.tone] || C.secondary }}>
              {note.text}
            </Typography>
          )}
        </Box>
      </Box>
    </CardButton>
  );
}

/** Responsive card grid: 2 columns on phones, 3 on tablets, 4 on desktop. */
export function LotGrid({ children, columns = { xs: 2, sm: 3, lg: 4 } }) {
  return (
    <Box
      role="list"
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: `repeat(${columns.xs}, minmax(0, 1fr))`,
          sm: `repeat(${columns.sm}, minmax(0, 1fr))`,
          lg: `repeat(${columns.lg}, minmax(0, 1fr))`,
        },
        gap: { xs: 1.5, sm: 2, md: 3 },
        "& > *": { minWidth: 0 },
      }}
    >
      {React.Children.map(children, (child) => (child ? <Box role="listitem" sx={{ display: "flex" }}>{child}</Box> : null))}
    </Box>
  );
}
